import * as React from 'react'
import {
  Bell,
  Bot,
  Check,
  Maximize2,
  Upload,
  Filter,
  Globe,
  Hash,
  MessageCircle,
  Phone,
  Search,
  Settings,
  Shield,
  Star,
  User,
  Users,
  WandSparkles,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { SelectorVisual } from '@/components/ui/selector-visual'
import type { StoredAgentVisual } from '@/lib/agent-visuals'


const STYLE_OPTIONS = [
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'pixel-art', label: 'Pixel Art' },
  { value: '3d-render', label: '3D Render' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'watercolor', label: 'Watercolor' },
  { value: 'cyberpunk', label: 'Cyberpunk' },
]

const ICON_MAP = {
  bot: Bot,
  filter: Filter,
  phone: Phone,
  globe: Globe,
  star: Star,
  zap: Zap,
  bell: Bell,
  message: MessageCircle,
  'message-circle': MessageCircle,
  user: User,
  users: Users,
  hash: Hash,
  check: Check,
  search: Search,
  settings: Settings,
  shield: Shield,
} as const

export function AgentVisualIcon({
  value,
  colorClass,
  className,
}: {
  value?: string
  colorClass?: string
  className?: string
}) {
  if (!value) return <Bot className={cn(className, 'text-muted-foreground')} />
  if (value.startsWith('emoji:')) {
    return <span className={cn('leading-none', className)}>{value.replace('emoji:', '')}</span>
  }
  const Icon = ICON_MAP[value as keyof typeof ICON_MAP] ?? Bot
  return <Icon className={cn(className, colorClass)} />
}

export function AgentAvatar({
  visual,
  fallbackClassName,
  className,
}: {
  visual?: StoredAgentVisual
  fallbackClassName?: string
  className?: string
}) {
  const [coverError, setCoverError] = React.useState(false)
  React.useEffect(() => { setCoverError(false) }, [visual?.coverUrl])
  if (visual?.coverUrl && !coverError) {
    return (
      <img
        src={visual.coverUrl}
        alt="Agent cover"
        className={cn('h-full w-full object-cover', className)}
        onError={() => setCoverError(true)}
      />
    )
  }
  return (
    <div className={cn('flex h-full w-full items-center justify-center', fallbackClassName)}>
      <AgentVisualIcon value={visual?.icon} colorClass={visual?.colorClass} className="h-6 w-6 text-sm" />
    </div>
  )
}

interface AgentVisualEditorProps {
  visual: StoredAgentVisual
  onVisualChange: (next: StoredAgentVisual) => void
  prompt: string
  onPromptChange: (value: string) => void
  style: string
  onStyleChange: (value: string) => void
  generating: boolean
  generateError?: string | null
  onGenerate: () => void
}

function PixelArtLoading({ className }: { className?: string }) {
  return (
    <div className={cn('relative overflow-hidden rounded-lg border bg-background/90', className)}>
      <style>{`
        @keyframes simple-loader-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative flex items-center gap-2">
          <span
            className="h-5 w-5 rounded-full border-2 border-muted-foreground/25 border-t-foreground"
            style={{ animation: 'simple-loader-spin 1s linear infinite' }}
          />
          <span className="text-xs text-muted-foreground">Generando imagen...</span>
        </div>
      </div>
    </div>
  )
}

export function AgentVisualEditor({
  visual,
  onVisualChange,
  prompt,
  onPromptChange,
  style,
  onStyleChange,
  generating,
  generateError,
  onGenerate,
}: AgentVisualEditorProps) {
  const isMobile = useIsMobile()
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [imageOpen, setImageOpen] = React.useState(false)
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const touchScrollStyle = React.useMemo(() => ({ WebkitOverflowScrolling: 'touch' as const, touchAction: 'pan-y' as const }), [])

  return (
    <div className={cn("rounded-lg border bg-card/50 space-y-4", isMobile ? "p-3" : "p-4")}>
      <div>
        <Label className="text-sm">Icono o emoji</Label>
        <div className={cn("mt-2", isMobile ? "space-y-2" : "flex flex-wrap items-center gap-2")}>
          {isMobile ? (
            <div className="flex items-center gap-2">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-muted/40">
                <AgentAvatar visual={visual} fallbackClassName="bg-muted/30" />
              </div>
              <div className="grid min-w-0 flex-1 grid-cols-3 gap-1">
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button size="xs" type="button" variant="outline" className="w-full text-[10px]">
                      <span className="truncate">Icono</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    portalled={!isMobile}
                    className="z-[120] w-[min(360px,calc(100vw-2rem))] max-h-[75dvh] overflow-auto overscroll-contain p-3"
                    align="start"
                    sideOffset={8}
                    style={touchScrollStyle}
                  >
                    <SelectorVisual
                      visual={visual}
                      onVisualChange={onVisualChange}
                      onElegido={() => setPickerOpen(false)}
                      renderIcono={(id, colorClass) => (
                        <AgentVisualIcon value={id} colorClass={colorClass} className="h-4 w-4" />
                      )}
                    />
                  </PopoverContent>
                </Popover>
                <Popover open={imageOpen} onOpenChange={setImageOpen}>
                  <PopoverTrigger asChild>
                    <Button size="xs" type="button" variant="outline" className="w-full text-[10px]">
                      <span className="truncate">Generar</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    portalled={!isMobile}
                    className="z-[120] w-[min(420px,calc(100vw-2rem))] max-h-[75dvh] overflow-auto overscroll-contain p-3 space-y-2"
                    align="start"
                    sideOffset={8}
                    style={touchScrollStyle}
                  >
                    <Input
                      value={prompt}
                      onChange={(e) => onPromptChange(e.target.value)}
                      placeholder="Describe la portada del agente..."
                    />
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Select value={style} onValueChange={onStyleChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Estilo" />
                        </SelectTrigger>
                        <SelectContent>
                          {STYLE_OPTIONS.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" onClick={onGenerate} disabled={generating || !prompt.trim()}>
                        {generating ? 'Generando...' : 'Enviar'}
                      </Button>
                    </div>
                    {generateError && <p className="text-xs text-destructive">{generateError}</p>}
                  </PopoverContent>
                </Popover>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (!file) return
                    const reader = new FileReader()
                    reader.onload = () => {
                      const value = typeof reader.result === 'string' ? reader.result : ''
                      if (value) {
                        onVisualChange({ ...visual, coverUrl: value, icon: undefined })
                      }
                    }
                    reader.readAsDataURL(file)
                    event.currentTarget.value = ''
                  }}
                />
                <Button size="xs" type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full text-[10px]">
                  <span className="truncate">Subir</span>
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <div className="h-16 w-16 overflow-hidden rounded-lg bg-muted/40">
                <AgentAvatar visual={visual} fallbackClassName="bg-muted/30" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline">Elegir icono</Button>
            </PopoverTrigger>
            <PopoverContent
              portalled={!isMobile}
              className="z-[120] w-[360px] max-h-[75dvh] overflow-auto overscroll-contain p-3"
              align="start"
              sideOffset={8}
              style={touchScrollStyle}
            >
              <SelectorVisual
                visual={visual}
                onVisualChange={onVisualChange}
                onElegido={() => setPickerOpen(false)}
                columnasIconos={10}
                columnasEmojis={8}
                renderIcono={(id, colorClass) => (
                  <AgentVisualIcon value={id} colorClass={colorClass} className="h-4 w-4" />
                )}
              />
            </PopoverContent>
          </Popover>
          <Popover open={imageOpen} onOpenChange={setImageOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline">
                <WandSparkles className="mr-2 h-4 w-4" />
                Generar imagen
              </Button>
            </PopoverTrigger>
            <PopoverContent
              portalled={!isMobile}
              className="z-[120] w-[420px] max-h-[75dvh] overflow-auto overscroll-contain p-3 space-y-2"
              align="start"
              sideOffset={8}
              style={touchScrollStyle}
            >
              <Input
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                placeholder="Describe la portada del agente..."
              />
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Select value={style} onValueChange={onStyleChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Estilo" />
                  </SelectTrigger>
                  <SelectContent>
                    {STYLE_OPTIONS.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={onGenerate} disabled={generating || !prompt.trim()}>
                  {generating ? 'Generando...' : 'Enviar'}
                </Button>
              </div>
              {generateError && <p className="text-xs text-destructive">{generateError}</p>}
            </PopoverContent>
          </Popover>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (!file) return
              const reader = new FileReader()
              reader.onload = () => {
                const value = typeof reader.result === 'string' ? reader.result : ''
                if (value) {
                  // Cover image and icon are mutually exclusive to avoid overlap confusion.
                  onVisualChange({ ...visual, coverUrl: value, icon: undefined })
                }
              }
              reader.readAsDataURL(file)
              event.currentTarget.value = ''
            }}
          />
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            Subir imagen
          </Button>
          </div>
          </div>
          )}
          {visual.icon && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onVisualChange({ ...visual, icon: undefined })}
            >
              Quitar icono
            </Button>
          )}
          {visual.coverUrl && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onVisualChange({ ...visual, coverUrl: undefined })}
            >
              Quitar imagen
            </Button>
          )}
        </div>
        {visual.coverUrl && (
          <div className="relative mt-2 overflow-hidden rounded-lg border">
            <img src={visual.coverUrl} alt="Cover preview" className="h-36 w-full object-cover" />
            <Button
              type="button"
              variant="secondary"
              size="icon-xs"
              className="absolute right-2 top-2 bg-black/55 text-white hover:bg-black/70"
              onClick={() => setPreviewOpen(true)}
              aria-label="Ver imagen en pantalla completa"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            {generating && (
              <div className="absolute inset-0 p-0">
                <PixelArtLoading className="h-full w-full border-0 rounded-none" />
              </div>
            )}
          </div>
        )}
        {generating && !visual.coverUrl && <PixelArtLoading className="mt-2 h-36 w-full" />}
      </div>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[95vw] w-[1100px] p-2 bg-black border-border">
          <DialogTitle className="sr-only">Vista ampliada de portada</DialogTitle>
          {visual.coverUrl ? (
            <img
              src={visual.coverUrl}
              alt="Agent cover full screen"
              className="max-h-[88vh] w-full rounded-md object-contain"
            />
          ) : (
            <div className="h-[70vh] w-full rounded-md bg-black" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
